import { AxiosPromise } from 'axios';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { switchAuth } from '../../../api/auth.api';
import {
  getMember,
  unlockAccount,
  updateMember,
} from '../../../api/members.api';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { getWebUrl } from '../../../lib/util';
import { IGetAuth } from '../../../models/auth.model';
import {
  IGetMember,
  IMember,
  IUpdateMemberInputBody,
} from '../../../models/members.model';
import Button from '../../Button';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import SearchPostcodeModal from '../../modals/SearchPostcodeModal';
import Radio from '../../Radio';
import { useTranslation } from 'react-i18next';
import TableEmpty from '../../common/TableEmpty';

type updateMemberBodyKey = keyof IUpdateMemberInputBody;

interface IProps {
  member?: IMember;
  isEdit: boolean;

  onEditClick(): void;

  memberExecute(promise: AxiosPromise<IGetMember>): Promise<void>;
}

const MemberInfoEditor: FC<IProps> = ({
  member,
  isEdit,
  onEditClick,
  memberExecute,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { successNotification, infoNotification } = useNotification();

  const {
    data: unlockRes,
    isLoading: unlockIsLoading,
    error: unlockError,
    execute: unlockExecute,
  } = useAxios();
  const {
    data: auth,
    isLoading: authIsLoading,
    error: authError,
    execute: authExecute,
  } = useAxios<IGetAuth>();
  const {
    data: updateMemberResult,
    isLoading: updateMemberIsLoading,
    error: updateMemberError,
    execute: updateMemberExecute,
  } = useAxios();

  const [postcodeVisible, setPostcodeVisible] = useState(false);
  const [memberInputReq, setMemberInputReq] = useState<
    IUpdateMemberInputBody | undefined
  >();

  useEffect(() => {
    if (member) {
      setMemberInputReq(member);
    }
    console.log(member);
    console.log(memberInputReq);
  }, [member]);

  useEffect(() => {
    if (auth && !authIsLoading) {
      const url = getWebUrl();
      if (url) window.location.href = url;
    }
  }, [auth, authIsLoading]);

  useEffect(() => {
    if (updateMemberResult && !updateMemberIsLoading) {
      history.push(`/homepage/member${location.search}&refresh=true`);
      if (member) memberExecute(getMember(member.memberCode));
      successNotification(t('common.message.save1'));
      onEditClick();
    }
  }, [updateMemberResult, updateMemberIsLoading]);

  useEffect(() => {
    if (unlockRes && !unlockIsLoading) {
      successNotification(t('campus.staff.message8'));
    }
  }, [unlockRes, unlockIsLoading]);

  const onCompletePostcode = ({ jibunAddress }: any) => {
    setMemberInputReq({ ...memberInputReq, address: jibunAddress });
    setPostcodeVisible(false);
  };

  const onGenderChange = (value: 'M' | 'F') => {
    setMemberInputReq({ ...memberInputReq, genderDiv: value });
  };

  const onConsentChange = (value: 'Y' | 'N') => {
    setMemberInputReq({ ...memberInputReq, consentYn: value });
  };

  const onSwitchAuth = (memberCode?: string) => {
    if (!memberCode) return;
    authExecute(switchAuth(memberCode));
  };

  const onUnlockAccount = (memberCode?: string) => {
    if (!memberCode) return;
    unlockExecute(unlockAccount(memberCode));
  };

  const onLocalEditClick = () => {
    if (isEdit) {
      if (!member) return;
      if (!memberInputReq?.memberKorName)
        return infoNotification(t('homepage.member.6'));
      if (!memberInputReq?.mobilePhone)
        return infoNotification(t('homepage.member.7'));
      if (!memberInputReq?.address)
        return infoNotification(t('homepage.member.8'));
      if (!memberInputReq?.genderDiv) return;
      if (!memberInputReq?.email)
        return infoNotification(t('homepage.member.9'));
      if (!memberInputReq?.consentYn) return;

      const form = new FormData();
      form.append('memberKorName', memberInputReq.memberKorName);
      form.append('genderDiv', memberInputReq.genderDiv);
      form.append('mobilePhone', memberInputReq.mobilePhone);
      form.append('email', memberInputReq.email);
      form.append('address', memberInputReq.address);
      form.append(
        'detailAddress',
        memberInputReq.detailAddress ? memberInputReq.detailAddress : '',
      );
      form.append('consentYn', memberInputReq.consentYn);
      const jsonData = JSON.stringify(Object.fromEntries(form));
      console.log(jsonData);

      updateMemberExecute(updateMember(member.memberCode, jsonData));
    } else {
      onEditClick();
    }
  };

  const onMemberInputChange = (
    { target: { value } }: ChangeEvent<HTMLInputElement>,
    key: updateMemberBodyKey,
  ) => {
    const newMemberReq = { ...memberInputReq };
    (newMemberReq ?? {})[key] = value;
    setMemberInputReq(newMemberReq);
  };

  let modifiedMember = { ...member };

  if (modifiedMember) {
    if (modifiedMember.memberType?.trim() === 'P') {
      modifiedMember.memberType = t('homepage.member.10');
    } else {
      if (modifiedMember.memberType?.trim() === 'S') {
        modifiedMember.memberType = t('homepage.member.11');
      } else {
        modifiedMember.memberType = '';
      }
    }
    if (modifiedMember.genderDiv?.trim() === 'M') {
      modifiedMember.genderDiv = t('homepage.member.12');
    } else {
      if (modifiedMember.genderDiv?.trim() === 'F') {
        modifiedMember.genderDiv = t('homepage.member.13');
      } else {
        modifiedMember.genderDiv = '';
      }
    }
    if (modifiedMember.consentYn?.trim() === 'Y') {
      modifiedMember.consentYn = t('homepage.member.14');
    } else {
      if (modifiedMember.consentYn?.trim() === 'N') {
        modifiedMember.consentYn = t('homepage.member.15');
      } else {
        modifiedMember.consentYn = '';
      }
    }
  }
  return (
    <div>
      {unlockIsLoading || authIsLoading || updateMemberIsLoading ? (
        <Loader />
      ) : null}
      <ErrorResponse error={unlockError} />
      <ErrorResponse error={authError} />
      <ErrorResponse error={updateMemberError} />

      <div className="comm-bx__between">
        <h3 className='sub-title'>{t('homepage.member.16')}</h3>
      </div>
      {member ? (
        <>
          <div className='comm-table__area rowType'>
            <table>
              <tbody>
                <tr>
                  <th>{t('homepage.member.17')}{isEdit ? '' : '(ID)'}</th>
                  <td>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control input-text"
                        value={
                          isEdit
                            ? memberInputReq?.memberKorName
                            : `${memberInputReq?.memberKorName} (${memberInputReq?.webId})`
                        }
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          onMemberInputChange(e, 'memberKorName')
                        }
                        disabled={!isEdit}
                      />
                      {member
                        ? !isEdit && (
                            <div className="comm-btn__area ml-2">
                              <button
                                className="btn-basic small mint"
                                onClick={() => onSwitchAuth(member?.memberCode)}
                              >
                                {t('homepage.member.18')}
                              </button>
                              {writeAuthYn === 'Y' ? (
                                <button
                                  className="btn-basic small mint"
                                  onClick={() =>
                                    onUnlockAccount(member?.memberCode)
                                  }
                                >
                                  {t('homepage.member.19')}
                                </button>
                              ) : null}
                            </div>
                          )
                        : null}
                    </div>
                  </td>
                  <th>{t('homepage.member.20')}</th>
                  <td>
                    <input
                      type="text"
                      className="form-control input-text"
                      value={memberInputReq?.mobilePhone}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onMemberInputChange(e, 'mobilePhone')
                      }
                      disabled={!isEdit}
                    />
                  </td>
                </tr>
                <tr>
                  <th>{t('homepage.member.21')}</th>
                  <td>{modifiedMember?.memberType}</td>
                  <th>{t('homepage.member.22')}</th>
                  <td>
                    {isEdit ? (
                      <div>
                        <Radio
                          checked={memberInputReq?.genderDiv?.trim() === 'M'}
                          onChange={() => onGenderChange('M')}
                          text={t('homepage.member.23')}
                          disabled={!isEdit}
                        />
                        <Radio
                          className="ml-3"
                          checked={memberInputReq?.genderDiv?.trim() === 'F'}
                          onChange={() => onGenderChange('F')}
                          text={t('homepage.member.24')}
                          disabled={!isEdit}
                        />
                      </div>
                    ) : (
                      modifiedMember?.genderDiv
                    )}
                  </td>
                </tr>
                <tr>
                  <th>{t('homepage.member.25')}</th>
                  <td colSpan={3}>
                    <div className='comm-btn__area align-items-center'>
                    {isEdit && (
                      <>
                        <Button
                          className="btn-basic black"
                          onClick={() => setPostcodeVisible(true)}
                          text={t('homepage.member.26')}
                        />{' '}
                      </>
                    )}
                    {memberInputReq?.address}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>{t('homepage.member.27')}</th>
                  <td colSpan={3}>
                    <input
                      type="text"
                      className="form-control input-text"
                      value={memberInputReq?.detailAddress}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onMemberInputChange(e, 'detailAddress')
                      }
                      disabled={!isEdit}
                    />
                  </td>
                </tr>
                <tr>
                  <th>{t('homepage.member.28')}</th>
                  <td>
                    <input
                      type="text"
                      className="form-control input-text"
                      value={memberInputReq?.email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onMemberInputChange(e, 'email')
                      }
                      disabled={!isEdit}
                    />
                  </td>
                  <th>{t('homepage.member.29')}</th>
                  <td>
                    {isEdit ? (
                      <div>
                        <Radio
                          checked={memberInputReq?.consentYn?.trim() === 'Y'}
                          onChange={() => onConsentChange('Y')}
                          text={t('homepage.member.30')}
                          disabled={!isEdit}
                        />
                        <Radio
                          className="ml-3"
                          checked={memberInputReq?.consentYn?.trim() === 'N'}
                          onChange={() => onConsentChange('N')}
                          text={t('homepage.member.31')}
                          disabled={!isEdit}
                        />
                      </div>
                    ) : (
                      modifiedMember?.consentYn
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="comm-bx__space">
            <div className="comm-btn__area right">
              <Button
                className={isEdit ? "btn-basic pink" : "btn-basic pink-line"}
                onClick={onLocalEditClick}
                text={isEdit ? t('common.button.save') : t('common.button.edit')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>

          <SearchPostcodeModal
            postcodeVisible={postcodeVisible}
            setPostcodeVisible={setPostcodeVisible}
            onCompletePostcode={onCompletePostcode}
          />
        </>
      ) : (
        <TableEmpty text={t('homepage.member.32')} />
      )}
    </div>
  );
};

export default MemberInfoEditor;
